import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getEventById, getEventImages, getEvents } from '../../services/gallery';
import Loading from '../Loading';
import NavBar from '../NavBar';
import FaceRecognition from './FaceRecognition';

function Gallery() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [images, setImages] = useState([]);
  const [model, setModel] = useState(false);
  const [tempImgSrc, setTempImgSrc] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [autoPlay, setAutoPlay] = useState(false);
  const [allEvents, setAllEvents] = useState([]);
  const [showFaceRecognition, setShowFaceRecognition] = useState(false);

  const autoPlayRef = useRef(null);
  const inactivityRef = useRef(null);
  const failedImages = useRef(new Set());

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const eventData = await getEventById(id);
        setEvent(eventData);
        const unsub = getEventImages(eventData.id, setImages);
        return () => unsub();
      } catch (error) {
        console.error("Error fetching event data:", error);
      }
    };
    fetchEvent();
  }, [id]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const unsub = getEvents((events) => {
      setAllEvents(events.filter(e => e.id !== id));
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (autoPlay && images.length > 0) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => {
          const nextIndex = (prev + 1) % images.length;
          setTempImgSrc(images[nextIndex].url);
          return nextIndex;
        });
      }, 3000);
    }
    return () => {
      if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    };
  }, [autoPlay, images]);

  const resetInactivityTimer = useCallback(() => {
    setAutoPlay(false);
    if (inactivityRef.current) clearTimeout(inactivityRef.current);
    inactivityRef.current = setTimeout(() => {
      if (model && images.length > 1) setAutoPlay(true);
    }, 5000);
  }, [model, images.length]);

  useEffect(() => {
    if (!model) {
      if (inactivityRef.current) clearTimeout(inactivityRef.current);
      setAutoPlay(false);
      setZoomLevel(1);
      return;
    }
    resetInactivityTimer();
    const handleActivity = () => resetInactivityTimer();
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [model, resetInactivityTimer]);

  const handlePrev = useCallback(() => {
    if (images.length === 0) return;
    const newIndex = currentIndex === 0 ? images.length - 1 : currentIndex - 1;
    setCurrentIndex(newIndex);
    setTempImgSrc(images[newIndex].url);
    setZoomLevel(1);
    resetInactivityTimer();
  }, [currentIndex, images, resetInactivityTimer]);

  const handleNext = useCallback(() => {
    if (images.length === 0) return;
    const newIndex = (currentIndex + 1) % images.length;
    setCurrentIndex(newIndex);
    setTempImgSrc(images[newIndex].url);
    setZoomLevel(1);
    resetInactivityTimer();
  }, [currentIndex, images, resetInactivityTimer]);

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 3));
    resetInactivityTimer();
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 1));
    resetInactivityTimer();
  };

  const getImg = (src, index) => {
    setTempImgSrc(src);
    setCurrentIndex(index);
    setModel(true);
  };

  const closeModal = () => {
    setModel(false);
    setTempImgSrc("");
    setZoomLevel(1);
  };

  const touchStartX = useRef(null);
  const handleImgError = (e) => { e.target.style.display = 'none'; };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) handlePrev();
      else handleNext();
    }
    touchStartX.current = null;
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = tempImgSrc;
    link.download = `fast-event-${Date.now()}.jpg`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!model) return;
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [model, handlePrev, handleNext]);

  useEffect(() => {
    if (!model) return;
    let scrollTimeout;
    const handleWheel = (e) => {
      if (Math.abs(e.deltaX) > 10) {
        e.preventDefault();
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (e.deltaX > 0) handleNext();
          else handlePrev();
        }, 50);
      }
    };
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      clearTimeout(scrollTimeout);
    };
  }, [model, handlePrev, handleNext]);

  if (!event) return <Loading />;

  const heroSrc = event.mainImage || images[0]?.url;

  return (
    <>
      {/* Lightbox */}
      {model && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-90 z-50"
          onClick={closeModal}
        >
          <div className="relative max-w-4xl w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3 text-white">
              <div className="flex items-center gap-3">
                <span className="text-sm text-white/70">{currentIndex + 1} / {images.length}</span>
                {autoPlay && <span className="text-green-400 text-xs animate-pulse">Playing</span>}
              </div>
              <div className="flex gap-4 items-center">
                <button
                  onClick={e => { e.stopPropagation(); handleDownload(); }}
                  className="text-white/80 hover:text-white text-sm flex items-center gap-1.5 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
                <div className="flex items-center gap-1 text-white/80">
                  <button
                    onClick={e => { e.stopPropagation(); handleZoomOut(); }}
                    className={`px-2 hover:text-white transition-colors ${zoomLevel <= 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    disabled={zoomLevel <= 1}
                  >−</button>
                  <span className="text-xs w-10 text-center">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={e => { e.stopPropagation(); handleZoomIn(); }}
                    className={`px-2 hover:text-white transition-colors ${zoomLevel >= 3 ? 'opacity-30 cursor-not-allowed' : ''}`}
                    disabled={zoomLevel >= 3}
                  >+</button>
                </div>
                <button onClick={closeModal} className="text-white/70 hover:text-white text-2xl leading-none transition-colors">&times;</button>
              </div>
            </div>

            <button
              className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-12 text-white/60 hover:text-white text-5xl transition-colors"
              onClick={e => { e.stopPropagation(); handlePrev(); }}
            >&#8249;</button>

            <div
              className="overflow-auto max-h-[80vh] flex items-center justify-center"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <img
                src={tempImgSrc}
                className="max-w-full max-h-[80vh] object-contain rounded-lg transition-transform"
                style={{ transform: `scale(${zoomLevel})`, cursor: zoomLevel < 3 ? 'zoom-in' : 'zoom-out' }}
                alt="Full size"
                onClick={e => { e.stopPropagation(); if (zoomLevel < 3) handleZoomIn(); else setZoomLevel(1); }}
                onError={handleImgError}
              />
            </div>

            <button
              className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-12 text-white/60 hover:text-white text-5xl transition-colors"
              onClick={e => { e.stopPropagation(); handleNext(); }}
            >&#8250;</button>

            <div className="mt-3 overflow-x-auto scrollbar-hide">
              <div className="flex gap-1.5 justify-center">
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image.url}
                    alt={`Thumbnail ${index + 1}`}
                    className={`w-12 h-12 object-cover cursor-pointer rounded transition-all ${index === currentIndex ? 'ring-2 ring-white opacity-100' : 'opacity-40 hover:opacity-70'}`}
                    onClick={e => {
                      e.stopPropagation();
                      setCurrentIndex(index);
                      setTempImgSrc(image.url);
                      setZoomLevel(1);
                      resetInactivityTimer();
                    }}
                    onError={handleImgError}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* NavBar */}
      <div className={`fixed top-0 left-0 w-full z-50 ${model ? "hidden" : ""}`}>
        <NavBar activeSection="" />
      </div>

      {/* Hero */}
      <div className="relative w-full h-[50vh] md:h-[65vh] overflow-hidden">
          {heroSrc ? (
          <img src={heroSrc} alt={event.title} className="w-full h-full object-cover" onError={handleImgError} />
        ) : (
          <div className="w-full h-full bg-mainBlue" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-8 md:pb-12">
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-2">
            {event.title}
          </h1>
          {event.description && (
            <p className="text-white/70 text-sm md:text-base max-w-2xl">{event.description}</p>
          )}
          {event.date && (
            <p className="text-white/50 text-xs mt-2">{event.date}</p>
          )}
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex items-center justify-between px-6 md:px-12 py-3 border-b border-gray-100 bg-white">
        <span className="text-gray-400 text-sm">
          {images.length > 0 ? `${images.length} photo${images.length !== 1 ? 's' : ''}` : 'Loading…'}
        </span>
      </div>

      {/* Photo grid */}
      <div className="bg-white px-1 md:px-3 pt-2 pb-12">
        <ul className="md:columns-3 columns-2">
          {images.map((image, index) => (
            <li
              key={index}
              className="group relative cursor-pointer overflow-hidden rounded m-1"
              onClick={() => getImg(image.url, index)}
            >
              <img
                src={image.url}
                alt={event.title}
                className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.04]"
                onError={handleImgError}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 8v6M8 11h6" />
                </svg>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* More Events */}
      {allEvents.length > 0 && (
        <div className="w-full border-t border-gray-100 bg-white py-10 md:py-14">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="text-xl md:text-2xl font-bold text-mainBlue mb-5 md:mb-7">More Events</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {allEvents.slice(0, 4).map(evt => (
                <Link
                  key={evt.id}
                  to={`/${evt.id}`}
                  className="group overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow duration-300"
                >
                  <div className="relative h-28 md:h-40 overflow-hidden">
                    <img
                      src={evt.mainImage}
                      alt={evt.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={handleImgError}
                    />
                  </div>
                  <div className="p-2.5 md:p-3">
                    <h3 className="text-xs md:text-sm font-semibold text-mainBlue line-clamp-2">{evt.title}</h3>
                    {evt.date && <p className="text-xs text-gray-400 mt-1">{evt.date}</p>}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      {process.env.REACT_APP_ENABLE_FACE_RECOGNITION === 'true' && !model && (
        <button
          onClick={() => setShowFaceRecognition(true)}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-5 py-3 bg-mainBlue text-white text-sm font-medium rounded-full shadow-lg hover:opacity-90 active:scale-95 transition-all"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 3H5a2 2 0 00-2 2v4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 3h4a2 2 0 012 2v4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 21H5a2 2 0 01-2-2v-4" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 21h4a2 2 0 002-2v-4" />
            <circle cx="12" cy="11" r="3" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.5 16.5c.83-1.19 2.03-2 3.5-2s2.67.81 3.5 2" />
          </svg>
          Find Me
        </button>
      )}

      {process.env.REACT_APP_ENABLE_FACE_RECOGNITION === 'true' && showFaceRecognition && (
        <FaceRecognition
          images={images}
          onClose={() => setShowFaceRecognition(false)}
          eventTitle={event?.title}
        />
      )}
    </>
  );
}

export default Gallery;
